import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Copy, Download, KeyRound, LifeBuoy, RefreshCw, ShieldCheck, Smartphone, X } from 'lucide-react';
import { staffApi, MySecurity, SecurityEvent } from '../../services/staffApi';
import { useAuth } from '../../contexts/AuthContext';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';

const EVENT_LABEL: Record<string, string> = {
  mfa_reset_by_admin: 'Two-step sign-in reset by an admin',
  recovery_code_used: 'Recovery code used',
  recovery_code_failed: 'Failed recovery attempt',
  authenticator_replaced: 'New authenticator set up',
  authenticator_enrolled: 'Authenticator set up',
  recovery_codes_regenerated: 'Recovery codes regenerated',
  recovery_expired: 'Recovery expired — signed out',
  mfa_not_enrolled: 'Signed out — no authenticator',
  email_mfa_revoked: 'Stray email code revoked',
};

/**
 * Every super admin's own two-step sign-in page: authenticator status,
 * recovery codes, and (for staff managers) the security log.
 * Always reachable — see ALWAYS_ALLOWED in panelPermissions.ts.
 */
export default function MySecurityPage() {
  const { email } = useAuth();
  const [sec, setSec] = useState<MySecurity | null>(null);
  const [events, setEvents] = useState<SecurityEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSec(await staffApi.security.mine());
    } catch (e: any) {
      setError(e?.message || 'Could not load your security settings');
    }
    // The log needs staff-management permission; hide it when refused.
    staffApi.security.events(50).then((r) => setEvents(r.events)).catch(() => setEvents(null));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await staffApi.security.regenerateCodes();
      setCodes(r.codes);
      setConfirmRegen(false);
      load();
    } catch (e: any) {
      setError(e?.message || 'Could not create recovery codes');
      setConfirmRegen(false);
    } finally {
      setBusy(false);
    }
  };

  const codesText = () => [
    'RubyTill Super Admin — recovery codes',
    `Account: ${email || ''}`,
    `Created: ${new Date().toLocaleString()}`,
    '',
    'Each code works once. Keep them somewhere safe and separate from your phone.',
    '',
    ...(codes || []),
  ].join('\n');

  const copyCodes = async () => {
    try { await navigator.clipboard.writeText(codesText()); setCopied(true); } catch { setCopied(false); }
  };
  const downloadCodes = () => {
    const url = URL.createObjectURL(new Blob([codesText()], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rubytill-super-admin-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const remaining = sec?.recoveryCodesRemaining ?? 0;
  const lowCodes = remaining <= 3;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header — black bar + ruby underline (InventoryChrome pattern) */}
      <div className="rounded-xl overflow-hidden shadow-sm">
        <div className="bg-black px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[#DC143C]" size={24} />
            <div>
              <h1 className="text-white text-lg font-semibold">My security</h1>
              <p className="text-stone-400 text-xs">Two-step sign-in and recovery codes for {email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            className="w-11 h-11 flex items-center justify-center rounded-lg text-stone-300 hover:text-white hover:bg-white/10"
            title="Refresh"
          >
            {loading ? <RubyGemMark size={18} drawing color="currentColor" /> : <RefreshCw size={18} />}
          </button>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading && !sec ? (
        <RubyLoader variant="section" />
      ) : sec && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white border border-stone-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-stone-900 font-semibold"><Smartphone size={18} /> Authenticator app</div>
            {sec.authenticatorEnabled ? (
              <p className="mt-2 text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={16} /> On — required at every sign-in</p>
            ) : (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5"><AlertTriangle size={16} /> Not active — sign out and in again to set it up</p>
            )}
            <p className="mt-3 text-xs text-stone-500">
              New phone? Sign in on the old one first, or use a recovery code to set up the new phone.
            </p>
          </div>

          <div className="bg-white border border-stone-200 rounded-xl p-5">
            <div className="flex items-center gap-2 text-stone-900 font-semibold"><KeyRound size={18} /> Recovery codes</div>
            <p className={`mt-2 text-sm flex items-center gap-1.5 ${lowCodes ? 'text-amber-700' : 'text-emerald-700'}`}>
              {lowCodes ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
              {remaining} of 10 left{remaining === 0 ? ' — create a set now' : lowCodes ? ' — create a new set soon' : ''}
            </p>
            {sec.recoveryCodesCreatedAt && (
              <p className="mt-1 text-xs text-stone-500">Created {new Date(sec.recoveryCodesCreatedAt).toLocaleDateString()}</p>
            )}
            <button
              type="button"
              disabled={!sec.authenticatorEnabled}
              onClick={() => setConfirmRegen(true)}
              className="mt-3 min-h-[44px] px-4 rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white text-sm font-medium disabled:opacity-50"
            >
              {remaining === 0 ? 'Create recovery codes' : 'Create new recovery codes'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-xl p-5 text-sm text-stone-700">
        <div className="flex items-center gap-2 text-stone-900 font-semibold mb-2"><LifeBuoy size={18} /> If you lose your phone</div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>On the sign-in page, enter your password, then choose <strong>Lost your phone or authenticator app?</strong></li>
          <li>Enter one recovery code. We email you a one-time code.</li>
          <li>Set up your authenticator again. You get a fresh set of recovery codes.</li>
        </ol>
        <p className="mt-2 text-stone-500">
          No codes left? Another super admin can reset your two-step sign-in from the Staff page. You and every other
          super admin are emailed whenever this happens.
        </p>
      </div>

      {events && (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200 font-semibold text-stone-900">Security log</div>
          {events.length === 0 ? (
            <p className="px-5 py-4 text-sm text-stone-500">No security events yet.</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {events.map((ev) => (
                <li key={ev.id} className="px-5 py-3 text-sm flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className={`font-medium ${ev.event === 'recovery_code_failed' || ev.event === 'email_mfa_revoked' ? 'text-red-600' : 'text-stone-900'}`}>
                    {EVENT_LABEL[ev.event] || ev.event}
                  </span>
                  <span className="text-stone-600">{ev.targetEmail || ev.targetLabel}</span>
                  {ev.actorEmail && ev.actorEmail !== ev.targetEmail && <span className="text-stone-500">by {ev.actorEmail}</span>}
                  <span className="text-stone-400 text-xs ml-auto">{new Date(ev.at).toLocaleString()}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirmRegen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setConfirmRegen(false)}>
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-stone-900">Create new recovery codes?</h3>
            <p className="text-sm text-stone-600">Your current codes stop working immediately. Every super admin is notified.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmRegen(false)} className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-stone-700">Cancel</button>
              <button type="button" onClick={regenerate} disabled={busy} className="min-h-[44px] px-4 rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white font-medium inline-flex items-center gap-2 disabled:opacity-60">
                {busy && <RubyGemMark size={16} drawing color="currentColor" />} Create codes
              </button>
            </div>
          </div>
        </div>
      )}

      {codes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="bg-black text-white px-5 py-3 flex items-center justify-between">
              <h3 className="font-semibold">Your new recovery codes</h3>
              <button type="button" onClick={() => setCodes(null)} className="w-11 h-11 -mr-3 flex items-center justify-center text-stone-300 hover:text-white"><X size={18} /></button>
            </div>
            <div className="h-[3px] bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-600">Shown only once. Each code works once. Save them away from your phone.</p>
              <div className="grid grid-cols-2 gap-2 bg-stone-50 border border-stone-200 rounded-lg p-4">
                {codes.map((c) => <code key={c} className="font-mono text-sm text-center text-stone-900 select-all">{c}</code>)}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={copyCodes} className="min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-stone-300 text-sm text-stone-700"><Copy size={16} /> {copied ? 'Copied' : 'Copy'}</button>
                <button type="button" onClick={downloadCodes} className="min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-stone-300 text-sm text-stone-700"><Download size={16} /> Download</button>
              </div>
              <button type="button" onClick={() => setCodes(null)} className="w-full min-h-[44px] rounded-lg bg-[#DC143C] hover:bg-[#9F1239] text-white font-medium">I have saved them</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
